/* ROUGHCUT — builds of this pattern, on the product page.
   ══════════════════════════════════════════════════════════════════════════
   Fetches the builds page once, keeps the reels tagged with this product,
   and renders them as a rail plus a full-screen viewer.

   The data has exactly one home: the blocks on the builds page. Tag a reel
   with a pattern there and it turns up here. Nothing is entered twice, so
   nothing can disagree.

   Defensive throughout, because this runs on the pages that take the money.
   Any failure — page moved, network down, markup changed, no builds for this
   pattern yet — ends with the section removing itself. A product page with
   no builds strip is normal. A product page with a broken empty strip is not.

   ES5, like the rest of the theme's assets.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var ICON = {
    play:  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6L19 12z" fill="currentColor" stroke="none"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    loud:  '<svg viewBox="0 0 24 24" aria-hidden="true" class="on"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 010 7"/></svg>',
    mute:  '<svg viewBox="0 0 24 24" aria-hidden="true" class="off"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M17 9l4 6M21 9l-4 6"/></svg>',
    prev:  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    next:  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>'
  };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Pull what we need out of one <article class="rr-reel"> from the builds
     page. Everything is optional — a reel with no caption or no maker is
     perfectly valid and must not break the card. */
  function readReel(art) {
    var out = { sources: [], poster: '', maker: '', makerUrl: '', caption: '' };

    var srcTag = art.querySelector('script.rr-src');
    if (srcTag) {
      try {
        var list = JSON.parse(srcTag.textContent || '[]');
        for (var i = 0; i < list.length; i++) {
          if (list[i] && list[i].url) out.sources.push(list[i]);
        }
      } catch (e) { /* malformed JSON on one reel shouldn't lose the rest */ }
    }

    var v = art.querySelector('video');
    if (v) out.poster = v.getAttribute('poster') || '';

    var m = art.querySelector('.rr-maker');
    if (m) {
      out.maker = (m.textContent || '').replace(/\s+/g, ' ').trim();
      if (m.tagName === 'A') out.makerUrl = m.getAttribute('href') || '';
    }
    var c = art.querySelector('.rr-cap');
    if (c) out.caption = (c.textContent || '').replace(/\s+/g, ' ').trim();

    return out.sources.length ? out : null;
  }

  /* Read one tile from the community photo wall. The archive already tags
     each tile with the pattern it was built from — data-p — which is the
     same handle the reels use, so photos and videos come from the same tag
     Jack applies once. */
  function readTile(el) {
    var full = el.getAttribute('data-full') || '';
    if (!full) {
      var im = el.querySelector('img');
      full = im ? (im.getAttribute('src') || '') : '';
    }
    if (!full) return null;
    var im2 = el.querySelector('img');
    return {
      sources: [],                       /* a photo has none — that's the flag */
      image: full,
      poster: im2 ? (im2.getAttribute('src') || full) : full,
      maker: (el.getAttribute('data-maker') || '').trim(),
      makerUrl: '',
      caption: ''
    };
  }

  /* Prefer the tallest progressive source under 1080p. The HLS entry is
     first in Shopify's list but a bare <video src> can't play it outside
     Safari, so picking sources[0] would give most visitors a dead player. */
  function bestSource(sources) {
    var best = null;
    for (var i = 0; i < sources.length; i++) {
      var s = sources[i];
      if (!s.url) continue;
      if (/\.m3u8($|\?)/i.test(s.url)) continue;
      var h = parseInt(s.height, 10) || 0;
      if (h > 1080) continue;
      if (!best || h > (parseInt(best.height, 10) || 0)) best = s;
    }
    if (best) return best.url;
    for (var j = 0; j < sources.length; j++) {
      if (sources[j].url && !/\.m3u8($|\?)/i.test(sources[j].url)) return sources[j].url;
    }
    return sources[0] ? sources[0].url : '';
  }

  function init(root) {
    var handle = root.getAttribute('data-handle') || '';
    /* No default here. An empty setting must mean "work it out yourself" so
       the candidate list below is used in order. Defaulting to a URL puts
       that guess at the FRONT of the queue — which is how the first version
       ended up asking for /pages/reels on every product page before trying
       the one that actually exists. */
    var source = root.getAttribute('data-source') || '';
    var max = parseInt(root.getAttribute('data-max'), 10) || 8;
    var photoSource = root.getAttribute('data-photos') || '';
    if (!handle) { root.remove(); return; }

    var rail = root.querySelector('.rc-pb__rail');
    var sub = root.querySelector('.rc-pb__sub');
    if (!rail) { root.remove(); return; }

    var builds = [];

    function give_up() { if (root.parentNode) root.remove(); }

    function render() {
      if (!builds.length) { give_up(); return; }

      var html = '';
      for (var i = 0; i < builds.length; i++) {
        var b = builds[i];
        html += '<button class="rc-pb__card" type="button" role="listitem" data-i="' + i + '"'
             +  ' aria-label="Watch ' + esc(b.maker || 'this build') + '">'
             +  (b.poster ? '<img src="' + esc(b.poster) + '" alt="" loading="lazy" decoding="async">' : '')
             +  (b.sources.length
                   ? '<span class="rc-pb__play">' + ICON.play + '</span>'
                   : '')
             +  '<span class="rc-pb__cred"><span class="rc-pb__who">'
             +  esc(b.maker || 'A build') + '</span></span>'
             +  '</button>';
      }
      rail.innerHTML = html;

      if (sub) {
        var t = sub.getAttribute('data-sub-template') || '';
        sub.textContent = t.replace('{count}', String(builds.length));
      }

      root.hidden = false;

      rail.addEventListener('click', function (e) {
        var card = e.target.closest ? e.target.closest('.rc-pb__card') : null;
        if (!card) return;
        openViewer(parseInt(card.getAttribute('data-i'), 10) || 0);
      });
    }

    /* ── the viewer ─────────────────────────────────────────────────────── */
    var v = null, idx = 0, loud = false;

    function buildViewer() {
      var el = document.createElement('div');
      el.className = 'rc-pbv';
      el.hidden = true;
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Build video');
      el.innerHTML =
        '<div class="rc-pbv__bars"></div>' +
        '<div class="rc-pbv__top">' +
          '<span class="rc-pbv__of"></span>' +
          '<div style="display:flex;gap:8px">' +
            '<button class="rc-pbv__sound" type="button" aria-label="Turn sound on">' + ICON.loud + ICON.mute + '</button>' +
            '<button class="rc-pbv__x" type="button" aria-label="Close">' + ICON.close + '</button>' +
          '</div>' +
        '</div>' +
        '<div class="rc-pbv__stage">' +
          '<video class="rc-pbv__video" playsinline preload="auto"></video>' +
          '<img class="rc-pbv__still" alt="" hidden>' +
          '<button class="rc-pbv__tap" type="button" aria-label="Play or pause"></button>' +
          '<div class="rc-pbv__spin" aria-hidden="true"></div>' +
          '<button class="rc-pbv__nav rc-pbv__nav--prev" type="button" aria-label="Previous build">' + ICON.prev + '</button>' +
          '<button class="rc-pbv__nav rc-pbv__nav--next" type="button" aria-label="Next build">' + ICON.next + '</button>' +
          '<div class="rc-pbv__hud"></div>' +
        '</div>';
      document.body.appendChild(el);
      return el;
    }

    function show(i) {
      if (i < 0 || i >= builds.length) return;
      idx = i;
      var b = builds[i];
      var vid = v.querySelector('.rc-pbv__video');

      var still = v.querySelector('.rc-pbv__still');
      var isPhoto = !b.sources.length && b.image;

      v.classList.remove('is-paused');
      v.classList.toggle('is-photo', !!isPhoto);

      if (isPhoto) {
        /* A photo has no sound and nothing to pause, so the transport
           controls would be lying. Hide them, show the still, stop the
           video from holding a decoder open behind it. */
        v.classList.remove('is-loading');
        try { vid.pause(); vid.removeAttribute('src'); vid.load(); } catch (e) {}
        vid.hidden = true;
        still.hidden = false;
        still.src = b.image;
        paintChrome(b, i);
        return;
      }

      still.hidden = true;
      still.removeAttribute('src');
      vid.hidden = false;
      v.classList.add('is-loading');

      vid.poster = b.poster || '';
      vid.src = bestSource(b.sources);
      vid.muted = !loud;
      vid.loop = true;
      var p = vid.play();
      if (p && p.catch) p.catch(function () {
        /* Autoplay with sound is blocked until the user has interacted.
           Fall back to muted rather than showing a dead frame. */
        vid.muted = true;
        loud = false;
        v.classList.remove('is-loud');
        vid.play().catch(function () { v.classList.add('is-paused'); });
      });

      paintChrome(b, i);
    }

    function paintChrome(b, i) {
      var hud = v.querySelector('.rc-pbv__hud');
      var of = v.querySelector('.rc-pbv__of');
      var bars = v.querySelector('.rc-pbv__bars');

      of.textContent = (i + 1) + ' / ' + builds.length;

      var bhtml = '';
      for (var k = 0; k < builds.length; k++) {
        bhtml += '<i class="' + (k < i ? 'is-done' : '') + '"><b></b></i>';
      }
      bars.innerHTML = bhtml;

      var who = b.makerUrl
        ? '<a class="rc-pbv__who" href="' + esc(b.makerUrl) + '" target="_blank" rel="noopener">' + esc(b.maker) + '</a>'
        : (b.maker ? '<span class="rc-pbv__who">' + esc(b.maker) + '</span>' : '');
      hud.innerHTML = who
        + (b.caption ? '<p class="rc-pbv__cap">' + esc(b.caption) + '</p>' : '');

      v.querySelector('.rc-pbv__nav--prev').disabled = (i === 0);
      v.querySelector('.rc-pbv__nav--next').disabled = (i === builds.length - 1);
    }

    function openViewer(i) {
      if (!v) {
        v = buildViewer();
        wireViewer();
      }
      v.hidden = false;
      document.body.style.overflow = 'hidden';
      show(i);
    }

    function closeViewer() {
      if (!v) return;
      var vid = v.querySelector('.rc-pbv__video');
      try { vid.pause(); vid.removeAttribute('src'); vid.load(); } catch (e) {}
      v.hidden = true;
      document.body.style.overflow = '';
    }

    function wireViewer() {
      var vid = v.querySelector('.rc-pbv__video');

      v.querySelector('.rc-pbv__x').addEventListener('click', closeViewer);
      v.querySelector('.rc-pbv__nav--prev').addEventListener('click', function () { show(idx - 1); });
      v.querySelector('.rc-pbv__nav--next').addEventListener('click', function () { show(idx + 1); });

      v.querySelector('.rc-pbv__sound').addEventListener('click', function () {
        loud = !loud;
        vid.muted = !loud;
        v.classList.toggle('is-loud', loud);
        this.setAttribute('aria-label', loud ? 'Turn sound off' : 'Turn sound on');
        if (loud) { var p = vid.play(); if (p && p.catch) p.catch(function () {}); }
      });

      v.querySelector('.rc-pbv__tap').addEventListener('click', function () {
        if (vid.paused) { vid.play(); v.classList.remove('is-paused'); }
        else { vid.pause(); v.classList.add('is-paused'); }
      });

      vid.addEventListener('waiting', function () { v.classList.add('is-loading'); });
      vid.addEventListener('playing', function () { v.classList.remove('is-loading'); });
      vid.addEventListener('error', function () {
        /* One dead video shouldn't strand someone on a black screen. */
        v.classList.remove('is-loading');
        if (idx < builds.length - 1) show(idx + 1); else closeViewer();
      });

      document.addEventListener('keydown', function (e) {
        if (v.hidden) return;
        if (e.key === 'Escape') closeViewer();
        if (e.key === 'ArrowRight') show(idx + 1);
        if (e.key === 'ArrowLeft') show(idx - 1);
      });

      /* Swipe: left/right moves between builds, down closes. Thresholds are
         generous because this sits over a video, where a small drag is more
         often a fumble than an instruction. */
      var x0 = 0, y0 = 0, tracking = false;
      v.addEventListener('touchstart', function (e) {
        if (!e.touches || !e.touches[0]) return;
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; tracking = true;
      }, { passive: true });
      v.addEventListener('touchend', function (e) {
        if (!tracking || !e.changedTouches || !e.changedTouches[0]) return;
        tracking = false;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dy) > 90 && Math.abs(dy) > Math.abs(dx)) { if (dy > 0) closeViewer(); return; }
        if (Math.abs(dx) > 60) show(dx < 0 ? idx + 1 : idx - 1);
      }, { passive: true });
    }

    /* ── go and get the data ────────────────────────────────────────────
       TWO sources, because the UGC lives in two places and Jack tags it in
       both: videos as blocks on the builds page, photos as blocks in the
       archive at the bottom of the community page.

       Each is tried against a LIST of candidate URLs rather than one. The
       builds page is reachable at /pages/builds and /pages/reels depending
       on which handle the page ended up with, and the community page at
       /pages/the-community or /pages/community. Guessing one and being
       wrong is silent: the fetch 404s, the section deletes itself, and the
       product page simply has no UGC on it with nothing to indicate why.
       That is exactly what happened on the first version of this. */

    function fetchFirst(urls, done) {
      var i = 0;
      function attempt() {
        if (i >= urls.length) { done(null); return; }
        var url = urls[i++];
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (t) { t ? done(t) : attempt(); })
          .catch(attempt);
      }
      attempt();
    }

    function q(sel, doc) {
      try { return doc.querySelectorAll(sel); } catch (e) { return []; }
    }

    function load() {
      var esc = handle.replace(/["\\]/g, '');
      var pending = 2;
      var vids = [], pics = [];

      function settle() {
        if (--pending > 0) return;
        /* Videos first: watching someone make the thing is stronger proof
           than a still of it finished. Photos fill in behind. */
        builds = vids.concat(pics).slice(0, max);
        render();
      }

      var buildsUrls = [];
      if (source) buildsUrls.push(source);
      buildsUrls.push('/pages/builds', '/pages/reels');

      fetchFirst(buildsUrls, function (html) {
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var arts = q('article.rr-reel[data-product="' + esc + '"]', doc);
          for (var i = 0; i < arts.length; i++) {
            var b = readReel(arts[i]);
            if (b) vids.push(b);
          }
        }
        settle();
      });

      var commUrls = [];
      if (photoSource) commUrls.push(photoSource);
      commUrls.push('/pages/the-community', '/pages/community');

      fetchFirst(commUrls, function (html) {
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var tiles = q('.rc-pw__tile[data-p="' + esc + '"]', doc);
          for (var i = 0; i < tiles.length; i++) {
            var t = readTile(tiles[i]);
            if (t) pics.push(t);
          }
        }
        settle();
      });
    }

    /* ── when to fetch ──────────────────────────────────────────────────
       This used to wait for an IntersectionObserver on the section itself,
       so the request only happened if you scrolled far enough to see it.
       That was wrong in a way that produced no error and no output at all.

       The section renders with `hidden`, which is display:none, which means
       it has NO BOX. An element with no box can never intersect anything,
       so the observer callback never fired, the fetch never ran, and the
       section stayed hidden forever. Every product page, silently.

       Worse, my own tests passed: the jsdom IntersectionObserver stub fired
       its callback immediately regardless of layout, which is precisely the
       part that was broken. A stub that can't reproduce the failure can't
       catch it.

       So: fetch when the browser is idle instead. It's one HTML document,
       no media — the videos on the builds page are preload="none" — and it
       happens after the page has settled, so it costs the product page
       nothing that matters. requestIdleCallback where available, a short
       timeout everywhere else. */
    if (!window.fetch || !window.DOMParser) { give_up(); return; }

    function kick() { try { load(); } catch (e) { give_up(); } }

    if (window.requestIdleCallback) {
      window.requestIdleCallback(kick, { timeout: 2500 });
    } else {
      setTimeout(kick, 800);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('.rc-pb');
    for (var i = 0; i < roots.length; i++) {
      /* Once per section, however many times boot is invited.

         Shopify's theme editor re-runs a section's scripts on every setting
         change, and a document can fire DOMContentLoaded after this file has
         already decided it was ready. Without this, the second pass builds a
         SECOND full-screen viewer over the first, binds a second click
         handler to the same rail, and fires a second fetch of the builds
         page — so one tap opens two stacked players and the progress bars
         double. Caught exactly that in testing. */
      if (roots[i].getAttribute('data-rc-pb-init') === '1') continue;
      roots[i].setAttribute('data-rc-pb-init', '1');
      try { init(roots[i]); } catch (e) {
        if (window.console && console.warn) console.warn('[rc-product-builds]', e);
        try { roots[i].remove(); } catch (e2) {}
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
